import React, { useEffect, useMemo, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  fetchConfirmedOrders,
  manualConfirmOrder,
  deleteAllConfirmedOrders,
  confirmOrderErrorMessage,
  deleteConfirmedOrdersErrorMessage,
  confirmedOrdersLoadErrorMessage,
} from '../lib/confirmedOrderApi';
import type { QuotationListItemDto } from '../lib/quotationApi';
import {
  createVendorPurchaseOrdersFromConfirmed,
  createVendorPurchaseOrdersErrorMessage,
} from '../lib/vendorPurchaseOrderApi';
import {
  Check,
  ChevronRight,
  ClipboardList,
  Download,
  ExternalLink,
  MoreVertical,
  SlidersHorizontal,
  Trash2,
  Upload,
} from 'lucide-react';
import { ModuleInfoButton } from '../components/ModuleInfoButton';
import LoadingState from '../components/LoadingState';
import ErrorModal from '../components/ErrorModal';
import { Modal } from '../components/Modal';
import QuotationSearchSelect from '../components/QuotationSearchSelect';
import Button from '../components/ui/Button';
import Pagination from '../components/ui/Pagination';
import SearchInput from '../components/ui/SearchInput';
import { EmptyState, ErrorState } from '../components/ui/States';
import { useToast } from '../context/ToastContext';
import { orderPoSendStatus, ORDER_PO_STATUS_LABEL, type PoSendStatus } from '../utils/poSendStatus';
import StatusBadge from '../components/StatusBadge';
import { StickerColorDot } from '../components/StickerColorDot';
import { formatCurrency } from '../utils/priceUtils';
import { buildSupplierCanonicalizer } from '../utils/supplierDedup';
import { formatDate as fmtDate } from "../utils/dateFormat";
import { toCsv } from '../utils/csv';
import { clientDisplayName, orderRef } from '../utils/orderIdentity';
import DateInput from "../components/DateInput";

const INFO = (
  <div className="flex flex-col gap-3">
    <p>Confirmed orders are client acceptances of sent quotations.</p>
    <ul className="list-disc list-inside space-y-1 pl-1">
      <li>View all confirmed orders with client, vessel, and totals</li>
      <li>Export the list to CSV</li>
      <li>Create Vendor Purchase Orders from a confirmed order</li>
    </ul>
  </div>
);

const formatDate = (value?: string) => {
  return fmtDate(value);
};

// D104.25: the one money formatter (US$/TT$ to the cent), never Intl's bare rounded "$".
const formatMoney = (value?: number, currency = 'USD') =>
  typeof value === 'number' ? formatCurrency(value, currency) : '--';

// Item 45 — one column's sort key reads the SAME value its cell shows (`orderRef`,
// `clientDisplayName`), never a second reading of the row.
const SORT_VALUE: Record<string, (row: any) => string | number | null> = {
  orderRef: (row) => orderRef(row) || null,
  clientName: (row) => clientDisplayName(row.clientName) || null,
  vesselName: (row) => row.vesselName || null,
  confirmedAt: (row) => (row.confirmedAt ? new Date(row.confirmedAt).getTime() : null),
  totalQuote: (row) => (typeof row.totalQuote === 'number' ? row.totalQuote : null),
  totalItems: (row) => (typeof row.totalItems === 'number' ? row.totalItems : null),
};

export const ConfirmedOrdersPage: React.FC = () => {
  const navigate = useNavigate();
  const moreRef = useRef<HTMLDivElement>(null);

  // Each row carries the vendor POs it has (`poSummary`, D104.25) — the server reads them for
  // THIS page of orders. They used to be a second request for the newest 500 POs of ALL orders
  // (CO-07, E1), which an order outside the 500 silently read as "none".
  const [rows, setRows] = useState<any[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [search, setSearch] = useState('');
  const [creatingId, setCreatingId] = useState<string | null>(null);
  const [successModal, setSuccessModal] = useState<{ count: number; confirmedId: string; rfqRef?: string } | null>(null);
  const [uploadOpen, setUploadOpen] = useState(false);
  // The quotation picked in the Upload dialog (an order is made from one that is not yet confirmed).
  const [pickedQuotation, setPickedQuotation] = useState<QuotationListItemDto | null>(null);
  const [uploading, setUploading] = useState(false);
  const [moreOpen, setMoreOpen] = useState(false);
  // QA retest item 45: the list had no sortable columns at all. Client-side, over the
  // currently loaded page only (like the search bar above) — there is no server sort
  // param for this list (`confirmedOrder.service.js listConfirmedOrders` always orders
  // by `confirmedAt desc`); adding one is its own backend change, not this item's.
  const [sortBy, setSortBy] = useState<string | null>(null);
  const [sortDir, setSortDir] = useState<1 | -1>(1);
  const handleSort = (column: string) => {
    setSortDir(sortBy === column ? ((sortDir * -1) as 1 | -1) : 1);
    setSortBy(column);
  };
  // House convention (MasterCataloguePage's SortableHeader): a clickable label,
  // an indicator beside it (▲ asc / ▼ desc / ↕ not the active column).
  const SortHeader = ({ label, column, className }: { label: string; column: string; className?: string }) => {
    const isActive = sortBy === column;
    return (
      <th className={className}>
        <button
          type="button"
          onClick={() => handleSort(column)}
          className="inline-flex items-center gap-1 font-semibold hover:opacity-80"
          title={`Sort by ${label}`}
        >
          {label}
          <span className={isActive ? 'text-primary-500' : 'text-slate-400'}>
            {isActive ? (sortDir === 1 ? '▲' : '▼') : '↕'}
          </span>
        </button>
      </th>
    );
  };
  // TECH_DEBT #211. All three actions on this page used to answer a failure with
  // `alert(e?.message)` — the server's raw prose in a browser dialog, which
  // invariant 6 forbids and which no user can act on. Failures now go to the
  // ErrorModal the rest of the app uses, with copy chosen by the envelope's
  // `code`; the two silent successes get a toast, because "nothing visibly
  // happened" is its own bug report.
  const [actionError, setActionError] = useState<{ title: string; message: string } | null>(null);
  const toast = useToast();
  const [filterOpen, setFilterOpen] = useState(false);
  const filterRef = useRef<HTMLDivElement>(null);
  // One key per visible column, so the filter panel and the table cannot drift
  // apart. `emailBy` left with the Email By column (Deborah, 2026-09-04: "I
  // don't think email is the thing you want to see every time"); `orderRef`
  // arrived with the column that replaced the row-index `#`.
  const [colFilters, setColFilters] = useState({
    orderRef: '', clientName: '', vesselName: '',
    createdFrom: '', createdTo: '',
    totalQuote: '', totalItems: '',
  });
  const setCol = (key: keyof typeof colFilters, val: string) =>
    setColFilters((p) => ({ ...p, [key]: val }));
  const clearFilters = () => setColFilters({ orderRef: '', clientName: '', vesselName: '', createdFrom: '', createdTo: '', totalQuote: '', totalItems: '' });
  const activeFilterCount = Object.values(colFilters).filter(Boolean).length;

  // Every real Total Quote / Total Items is >= 0, so a negative "Min" threshold
  // can only ever mean "match everything" — confusing to type by accident and
  // never what a user actually wants. The server (confirmedOrder.service.js
  // listConfirmedOrders) keeps a negative threshold's sign correctly rather
  // than silently flipping it (CO-05); this stops it reaching the server at
  // all, with a plain reason why, rather than quietly matching everything.
  //
  // Checked on the typed character, not the parsed number: `Number('-')` is
  // NaN, so a check against the parsed value only caught the sign once a
  // digit followed it — the field stayed silent for the "-" keystroke itself,
  // which is the one that should never be typed here at all.
  const startsWithMinus = (v: string) => v.trim().startsWith('-');
  const totalQuoteError = startsWithMinus(colFilters.totalQuote)
    ? 'Negative values are not allowed' : null;
  const totalItemsError = startsWithMinus(colFilters.totalItems)
    ? 'Negative values are not allowed' : null;
  // The Order field matches an RFQ/quotation number (e.g. "RFQ-0042"), where a
  // hyphen is a normal part of the reference — only a LEADING minus is
  // rejected, the same character used as a threshold's sign everywhere else
  // on this panel, and just as meaningless at the start of an order number.
  const orderRefError = startsWithMinus(colFilters.orderRef)
    ? 'Order cannot start with a minus sign' : null;
  const hasFilterError = !!totalQuoteError || !!totalItemsError || !!orderRefError;

  const [page, setPage] = useState(1);
  const [limit] = useState(25);
  const [total, setTotal] = useState(0);
  const totalPages = useMemo(() => Math.max(1, Math.ceil(total / limit)), [total, limit]);

  useEffect(() => { if (page > totalPages) setPage(totalPages); }, [totalPages, page]);

  // CO-07 — `search` and `colFilters` used to sit directly in the reload effect's
  // dependency array below, so every keystroke re-fetched the confirmed-orders
  // page immediately (plus a second time 350ms later, when the old page-reset
  // timer fired and changed `page`). Typing now only reloads once, 350ms after
  // the user stops, at page 1 — never mid-keystroke.
  const [debouncedSearch, setDebouncedSearch] = useState(search);
  const [debouncedColFilters, setDebouncedColFilters] = useState(colFilters);
  useEffect(() => {
    const t = setTimeout(() => {
      setDebouncedSearch(search);
      // A leading minus on Min Total Quote/Items or Order never reaches the
      // server at all — the field shows its own error (below) instead of the
      // filter silently matching every row (Min Total Quote/Items) or being
      // sent as a search term nothing will ever match (Order). Every OTHER
      // filter still applies normally even while one field is invalid.
      const next = {
        ...colFilters,
        orderRef: startsWithMinus(colFilters.orderRef) ? '' : colFilters.orderRef,
        totalQuote: startsWithMinus(colFilters.totalQuote) ? '' : colFilters.totalQuote,
        totalItems: startsWithMinus(colFilters.totalItems) ? '' : colFilters.totalItems,
      };
      // The reload effect below watches this object's IDENTITY, so equal values must keep the
      // SAME object: this timer also fires 350ms after mount (nothing typed), and a fresh-but-
      // equal object reloaded the list a second time on every page open.
      setDebouncedColFilters((prev) =>
        (Object.keys(next) as (keyof typeof next)[]).every((k) => prev[k] === next[k]) ? prev : next);
      setPage(1);
    }, 350);
    return () => clearTimeout(t);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [search, colFilters]);

  // The confirmed-orders page itself — the page's one read; it depends on
  // page/limit/search/filters. `quiet` re-reads the page already on screen without blanking it
  // to "Loading…" first (the re-read after Create PO, behind the success dialog). An options
  // object, not a bare boolean: ErrorState's Try again passes its click event as the argument.
  const loadConfirmedOrders = async ({ quiet = false }: { quiet?: boolean } = {}) => {
    if (!quiet) setLoading(true);
    setError(null);
    try {
      // CO-05 — colFilters used to only ever be applied to the rows already
      // fetched for this page; now sent to the server alongside search and
      // paging so a match on an unloaded page is no longer invisible.
      const confirmedData = await fetchConfirmedOrders(page, limit, debouncedSearch, false, {
        orderRef: debouncedColFilters.orderRef,
        clientName: debouncedColFilters.clientName,
        vesselName: debouncedColFilters.vesselName,
        createdFrom: debouncedColFilters.createdFrom,
        createdTo: debouncedColFilters.createdTo,
        minTotalQuote: debouncedColFilters.totalQuote,
        minTotalItems: debouncedColFilters.totalItems,
      });
      setRows(confirmedData.confirmedOrders || []);
      setTotal(confirmedData.pagination?.total || 0);
    } catch (e: any) {
      // The fourth leak on this page and the one every user meets first: this
      // banner used to print `e.message`, so a search string Mongo could not
      // parse rendered its regex complaint across the top of the screen.
      setError(confirmedOrdersLoadErrorMessage(e));
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadConfirmedOrders();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [page, limit, debouncedSearch, debouncedColFilters]);

  // Raising POs takes seconds and paging or searching is not locked meanwhile, so the re-read after
  // it must be of the page the user is on NOW, not the one this render's closure was made for
  // (the same pattern as RfqInboxPage's refreshSelectedRef, D103.15).
  const loadRef = useRef(loadConfirmedOrders);
  loadRef.current = loadConfirmedOrders;

  // Each order's vendor-PO send status (Ticket B), worked out while rendering from the PO facts
  // ITS OWN row arrived with — not copied into state by an effect, which ran a render AFTER
  // `rows` changed and drew a new page once with no status at all (E1). A row that arrived
  // without them (`poSummary` absent) has NOT had its POs read: it is left out of the map and its
  // cells say so, rather than claim "none" (E1).
  //
  // The supplier-name canonicalizer is built from THIS order's names alone (its vendor split and
  // its POs'): canonical labels are only ever compared within one order, and a page-wide
  // canonicalizer let another order's names merge two of this order's suppliers.
  const poStatusById = useMemo(() => {
    const statusMap = new Map<string, PoSendStatus>();
    for (const r of rows as any[]) {
      const pos = r.poSummary?.pos as Array<{ vendorName?: string; status?: string }> | undefined;
      if (!pos) continue;
      const vendors: string[] = r.vendors || [];
      const canon = buildSupplierCanonicalizer(
        [...vendors, ...pos.map((p) => String(p.vendorName || ''))].filter(Boolean),
      );
      // Item 26 — client invoicing (`invoiceSummary`, same row, `listConfirmedOrders`) outranks
      // every supplier-PO state, so the label moves on once the order is invoiced/sent.
      statusMap.set(String(r._id), orderPoSendStatus(vendors, pos, canon.canonicalOf, r.invoiceSummary));
    }
    return statusMap;
  }, [rows]);

  // Close dropdowns on outside click
  useEffect(() => {
    const handler = (e: MouseEvent) => {
      if (moreRef.current && !moreRef.current.contains(e.target as Node)) setMoreOpen(false);
      if (filterRef.current && !filterRef.current.contains(e.target as Node)) setFilterOpen(false);
    };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, []);

  const filteredRows = useMemo(() => {
    let result = rows;

    // Global search bar
    const term = search.trim().toLowerCase();
    if (term) {
      result = result.filter((r) => {
        // The sender stays SEARCHABLE even though it is no longer a column —
        // the server searches `emailFrom` too, and dropping it here would make
        // the same query behave differently before and after a page load.
        const hay = [r.clientName, r.vesselName, r.rfqNumber, r.quotationNumber, r.emailFrom]
          .filter(Boolean).join(' ').toLowerCase();
        return hay.includes(term);
      });
    }

    // CO-05 — the per-column filters (Order, Client Name, Vessel Name, Created
    // Date, Min Total Quote, Min Total Items) used to be applied HERE, over
    // only whatever page happened to already be loaded — so a match sitting on
    // an unloaded page was invisible, and the digit-only strip below turned a
    // negative "Min Total Quote" into a positive one. `colFilters` is now sent
    // to the server in `load()` and `rows` already reflects it, paged over the
    // WHOLE matching set (confirmedOrder.service.js `listConfirmedOrders`).
    // Re-filtering `rows` by the same values here would be redundant at best —
    // and at worst, silently wrong again if this copy ever drifted from the
    // server's (exactly what happened to the negative-number case).

    // Item 45 — sorting, over this same loaded set, same column keys the
    // headers below send to `handleSort`.
    if (sortBy) {
      const get = SORT_VALUE[sortBy];
      if (get) {
        result = [...result].sort((a, b) => {
          const av = get(a);
          const bv = get(b);
          if (av == null && bv == null) return 0;
          if (av == null) return 1; // missing sorts last, either direction
          if (bv == null) return -1;
          if (typeof av === 'number' && typeof bv === 'number') return (av - bv) * sortDir;
          return String(av).localeCompare(String(bv)) * sortDir;
        });
      }
    }

    return result;
  }, [rows, search, colFilters, sortBy, sortDir]);

  const exportCsv = () => {
    // Header and row must name the same columns as the table above, in the same
    // order — a header array that disagrees with its rows is a silent data bug
    // in every spreadsheet it lands in.
    const header = ['Order', 'Client Name', 'Vessel Name', 'Created', 'Total Quote', 'Total Items'];
    const lines = filteredRows.map((r) => [
      orderRef(r) || '', clientDisplayName(r.clientName), r.vesselName || '',
      formatDate(r.confirmedAt),
      r.totalQuote ?? '', r.totalItems ?? '',
    ]);
    // toCsv, not a private escaper — see utils/csv. (TECH_DEBT #38)
    const csv = toCsv([header, ...lines]);
    const blob = new Blob([csv], { type: 'text/csv;charset=utf-8;' });
    const url = window.URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `confirmed_orders_${Date.now()}.csv`;
    a.click();
    window.URL.revokeObjectURL(url);
  };

  const handleCreatePo = async (confirmedId: string) => {
    if (creatingId) return;
    setCreatingId(confirmedId);
    try {
      const data = await createVendorPurchaseOrdersFromConfirmed(confirmedId);
      const sourceRow = rows.find((r) => r._id === confirmedId);
      const rfqRef = sourceRow?.rfqNumber || sourceRow?.rfqId || null;
      setSuccessModal({ count: data.length || 0, confirmedId, rfqRef });
      // The order's PO facts are the server's: re-read this page, so the row says "PO created" and
      // its Supplier POs status agrees with it. Awaited, so the button stays on "Creating…" (and
      // every Create PO off) until the new summary lands — it cannot be pressed twice in between.
      await loadRef.current({ quiet: true });
    } catch (e: any) {
      setActionError({
        title: 'Could not create purchase orders',
        message: createVendorPurchaseOrdersErrorMessage(e),
      });
    } finally {
      setCreatingId(null);
    }
  };

  // Closing always forgets the pick, so reopening starts from an empty choice.
  const closeUpload = () => {
    setUploadOpen(false);
    setPickedQuotation(null);
  };

  const handleUploadNewOrder = async () => {
    if (!pickedQuotation) return;
    setUploading(true);
    try {
      await manualConfirmOrder({ quotationId: pickedQuotation._id });
      closeUpload();
      await loadConfirmedOrders();
      toast.success('Confirmed order created successfully.');
    } catch (e: any) {
      // The SAME endpoint the quotation page's Confirm Order button calls, so
      // it gets the same copy from the same place rather than a second wording
      // of "this quotation cannot be confirmed" invented here.
      setActionError({
        title: 'Could not confirm order',
        message: confirmOrderErrorMessage(e),
      });
    } finally {
      setUploading(false);
    }
  };

  const handleDeleteAll = async () => {
    setMoreOpen(false);
    const confirmed = window.prompt(
      'This will delete ALL confirmed orders. Type DELETE to confirm.'
    );
    if (confirmed !== 'DELETE') return;
    try {
      const { deleted } = await deleteAllConfirmedOrders();
      await loadConfirmedOrders();
      toast.success(`Deleted ${deleted} confirmed order${deleted === 1 ? '' : 's'}.`);
    } catch (e: any) {
      setActionError({
        title: 'Could not delete confirmed orders',
        message: deleteConfirmedOrdersErrorMessage(e),
      });
    }
  };

  // What the two PO cells (Supplier POs, Create vendor PO) show for a row that arrived without its
  // PO facts (a `brief` read, or a server that does not send them): nothing is claimed about the
  // order's POs and nothing is clickable, so "none" can never be read into "not read" (E1).
  const poUnread = <span className="text-xs text-slate-400">Unavailable</span>;

  return (
    <div className="h-full flex flex-col bg-brand-bg">

      {/* ── Header ───────────────────────────────────────────────── */}
      {/* The app's one header band: same gutter, same 20px title, same control
          heights as every other module (docs/DESIGN.md). */}
      <div className="shrink-0 border-b border-slate-200 bg-brand-bg px-4 pb-3 pt-4 sm:px-8">
        <div className="flex flex-wrap items-center justify-between gap-3">
        {/* Left: title */}
        <div className="flex items-center gap-2 shrink-0">
          <h1 className="text-xl font-semibold leading-7 text-slate-900">Confirmed Order</h1>
          <ModuleInfoButton content={INFO} ariaLabel="Confirmed Orders information" />
        </div>

        {/* Right: search + actions */}
        <div className="flex flex-wrap items-center gap-2">
          {/* Search — the placeholder is pinned by ConfirmedOrdersPage.CO07.test.tsx */}
          <SearchInput value={search} onChange={setSearch} placeholder="Search here" className="w-48" />

          {/* Filter button */}
          <div className="relative" ref={filterRef}>
            <Button
              onClick={() => setFilterOpen((p) => !p)}
              icon={<SlidersHorizontal aria-hidden="true" className="h-4 w-4" />}
              className={activeFilterCount ? 'border-primary-500 bg-primary-50 text-primary-500' : ''}
            >
              Filters
              {activeFilterCount > 0 && (
                <span className="flex h-[18px] min-w-[18px] items-center justify-center rounded-full bg-primary-500 px-1 text-xs font-semibold text-white">
                  {activeFilterCount}
                </span>
              )}
            </Button>

            {/* Filter panel */}
            {filterOpen && (
              <div className="absolute left-0 top-full mt-2 w-80 bg-white border border-slate-200 rounded-lg shadow-lg z-40 p-4 space-y-4">
                <div className="flex items-center justify-between">
                  <span className="text-xs font-semibold text-slate-700">Filter orders</span>
                  {activeFilterCount > 0 && (
                    <button onClick={clearFilters} className="text-xs font-medium text-slate-500 hover:text-slate-700">
                      Clear all
                    </button>
                  )}
                </div>

                {/* Order (quotation number; an RFQ number also matches) */}
                <div>
                  <label className="block text-xs font-medium text-slate-600 mb-1">Order</label>
                  <input
                    value={colFilters.orderRef}
                    onChange={(e) => setCol('orderRef', e.target.value)}
                    placeholder="e.g. QUO-00201"
                    aria-invalid={!!orderRefError}
                    className={`w-full h-8 rounded-md border px-2.5 text-xs focus:outline-none focus:ring-1 ${
                      orderRefError
                        ? 'border-rose-400 focus:ring-rose-400'
                        : 'border-slate-200 focus:ring-primary-500'
                    }`}
                  />
                  {orderRefError && (
                    <p className="mt-1 text-xs text-red-600">{orderRefError}</p>
                  )}
                </div>

                {/* Client Name */}
                <div>
                  <label className="block text-xs font-medium text-slate-600 mb-1">Client name</label>
                  <input
                    value={colFilters.clientName}
                    onChange={(e) => setCol('clientName', e.target.value)}
                    placeholder="e.g. Oceanic"
                    className="w-full h-8 rounded-md border border-slate-200 px-2.5 text-xs focus:outline-none focus:ring-1 focus:ring-primary-500"
                  />
                </div>

                {/* Vessel Name */}
                <div>
                  <label className="block text-xs font-medium text-slate-600 mb-1">Vessel name</label>
                  <input
                    value={colFilters.vesselName}
                    onChange={(e) => setCol('vesselName', e.target.value)}
                    placeholder="e.g. MV NORDIC"
                    className="w-full h-8 rounded-md border border-slate-200 px-2.5 text-xs focus:outline-none focus:ring-1 focus:ring-primary-500"
                  />
                </div>

                {/* Created Date Range — calendar pickers */}
                <div>
                  <label className="block text-xs font-medium text-slate-600 mb-1">Created date</label>
                  <div className="flex items-center gap-2">
                    <DateInput validity="filter"
                      value={colFilters.createdFrom}
                      onChange={(v) => setCol('createdFrom', v)}
                      className="flex-1 h-8 rounded-md border border-slate-200 px-2 text-xs focus:outline-none focus:ring-1 focus:ring-primary-500"
                    />
                    <span className="text-xs text-slate-500 shrink-0">to</span>
                    <DateInput validity="filter"
                      value={colFilters.createdTo}
                      onChange={(v) => setCol('createdTo', v)}
                      className="flex-1 h-8 rounded-md border border-slate-200 px-2 text-xs focus:outline-none focus:ring-1 focus:ring-primary-500"
                    />
                  </div>
                </div>

                {/* Total Quote — type="text", not "number": a number input SANITIZES
                    an incomplete value like a bare "-" back to "" before onChange ever
                    sees it (the value IDL getter returns "" whenever the typed content
                    isn't yet a complete valid number), so the minus keystroke itself
                    never reached validation — the error only appeared once a digit
                    followed it and the whole thing became a valid negative number.
                    inputMode keeps the numeric keypad on mobile; toNum (server) and
                    startsWithMinus (above) already handle an arbitrary typed string. */}
                <div>
                  <label className="block text-xs font-medium text-slate-600 mb-1">Min total quote (USD)</label>
                  <input
                    type="text"
                    inputMode="decimal"
                    value={colFilters.totalQuote}
                    onChange={(e) => setCol('totalQuote', e.target.value)}
                    placeholder="e.g. 1000"
                    aria-invalid={!!totalQuoteError}
                    className={`w-full h-8 rounded-md border px-2.5 text-xs focus:outline-none focus:ring-1 ${
                      totalQuoteError
                        ? 'border-rose-400 focus:ring-rose-400'
                        : 'border-slate-200 focus:ring-primary-500'
                    }`}
                  />
                  {totalQuoteError && (
                    <p className="mt-1 text-xs text-red-600">{totalQuoteError}</p>
                  )}
                </div>

                {/* Total Items — same reason as Total Quote above: type="text" so the
                    raw keystroke (including a bare "-") reaches validation. */}
                <div>
                  <label className="block text-xs font-medium text-slate-600 mb-1">Min total items</label>
                  <input
                    type="text"
                    inputMode="numeric"
                    value={colFilters.totalItems}
                    onChange={(e) => setCol('totalItems', e.target.value)}
                    placeholder="e.g. 5"
                    aria-invalid={!!totalItemsError}
                    className={`w-full h-8 rounded-md border px-2.5 text-xs focus:outline-none focus:ring-1 ${
                      totalItemsError
                        ? 'border-rose-400 focus:ring-rose-400'
                        : 'border-slate-200 focus:ring-primary-500'
                    }`}
                  />
                  {totalItemsError && (
                    <p className="mt-1 text-xs text-red-600">{totalItemsError}</p>
                  )}
                </div>

                {/* "Apply" exactly — ConfirmedOrdersPage.minFilterValidation.test.tsx
                    finds this button by that accessible name. */}
                <Button
                  variant="primary"
                  size="sm"
                  onClick={() => setFilterOpen(false)}
                  disabled={hasFilterError}
                  title={hasFilterError ? 'Fix the highlighted field before applying' : undefined}
                  className="w-full"
                >
                  Apply
                </Button>
              </div>
            )}
          </div>

          {/* Export */}
          <Button onClick={exportCsv} icon={<Download aria-hidden="true" className="h-4 w-4" />}>
            Export
          </Button>

          {/* Upload */}
          <Button
            variant="primary"
            onClick={() => setUploadOpen(true)}
            icon={<Upload aria-hidden="true" className="h-4 w-4" />}
          >
            Upload
          </Button>

          {/* ⋮ overflow — hides DELETE ALL */}
          <div className="relative" ref={moreRef}>
            <Button
              onClick={() => setMoreOpen((p) => !p)}
              aria-label="More actions"
              aria-haspopup="menu"
              aria-expanded={moreOpen}
              icon={<MoreVertical aria-hidden="true" className="h-4 w-4" />}
            />
            {moreOpen && (
              <div role="menu" className="absolute right-0 z-30 mt-1 w-44 rounded-lg border border-slate-200 bg-white py-1 shadow-lg">
                <button
                  role="menuitem"
                  onClick={handleDeleteAll}
                  className="flex w-full items-center gap-2 px-3 py-2 text-left text-sm text-red-700 hover:bg-red-50"
                >
                  <Trash2 aria-hidden="true" className="h-4 w-4" />
                  Delete all orders
                </button>
              </div>
            )}
          </div>
        </div>
        </div>
      </div>

      {/* ── Table ────────────────────────────────────────────────── */}
      <div className="flex-1 overflow-hidden">
        {loading ? (
          <LoadingState text="Loading confirmed orders..." />
        ) : error ? (
          <div className="p-4 sm:px-8">
            <ErrorState message={error} onRetry={loadConfirmedOrders} />
          </div>
        ) : (
          <div className="h-full overflow-auto bg-white">
            {/* table-fixed + equal col widths so every column is the same size.
                `data-table` (index.css) draws the header, the cells and the row
                hover — the same table as every other module. */}
            <table className="data-table table-fixed">
              {/* Comments moved above each <col> rather than trailing it: a trailing
                  comment leaves literal spaces (used to align the comments) as text
                  between elements, and <colgroup> may only contain <col>/<template> —
                  React warns "whitespace text nodes cannot be a child of <colgroup>"
                  and React 19 fails hydration over it. */}
              <colgroup>
                {/* Order */}
                <col className="w-[10%]" />
                {/* Client Name */}
                <col className="w-[13%]" />
                {/* Vessel Name */}
                <col className="w-[13%]" />
                {/* Created */}
                <col className="w-[10%]" />
                {/* Total Quote */}
                <col className="w-[11%]" />
                {/* Total Items */}
                <col className="w-[7%]" />
                {/* Status */}
                <col className="w-[13%]" />
                {/* Quotation */}
                <col className="w-[7%]" />
                {/* Create Vendor PO */}
                <col className="w-[11%]" />
                {/* Arrow */}
                <col className="w-10" />
              </colgroup>

              {/* ── Column headers ── */}
              <thead className="sticky top-0 z-10">
                <tr>
                  {/* "Order", not "#" (a row's page position is not an order number).
                      The label is `orderRef` — the QUO number, the one the
                      detail page titles itself with.

                      CATEGORY COLUMN INSERTION POINT — after Vessel Name below,
                      once the `ItemCategory` vocabulary is settled. Adding it
                      means: a <col> in the colgroup above, a <th> here, a <td>
                      in the row, a cell in the summary sub-row, a key in
                      `colFilters` + its panel input, a header AND a cell in
                      `exportCsv`, and colSpan on the empty-state row. */}
                  <SortHeader label="Order" column="orderRef" />
                  <SortHeader label="Client name" column="clientName" />
                  <SortHeader label="Vessel name" column="vesselName" />
                  <SortHeader label="Created" column="confirmedAt" />
                  <SortHeader label="Total quote" column="totalQuote" className="text-right" />
                  <SortHeader label="Total items" column="totalItems" className="text-right" />
                  <th>Supplier POs</th>
                  <th>Quotation</th>
                  <th>Create vendor PO</th>
                  <th className="w-10">
                    <span className="sr-only">Open</span>
                  </th>
                </tr>
              </thead>

              {/* ── Data rows ── */}
              <tbody>
                {filteredRows.length === 0 && (
                  <tr>
                    <td colSpan={10} className="px-4 py-10">
                      <EmptyState
                        title="No confirmed orders found"
                        detail="Try clearing the search or filters above."
                      />
                    </td>
                  </tr>
                )}

                {filteredRows.map((row) => (
                  <tr
                    key={row._id}
                    onClick={() => navigate(`/confirmed-orders/${row._id}`)}
                    className="cursor-pointer group"
                  >
                    {/* Order — `orderRef`: QUO number, RFQ only if none. */}
                    <td className="truncate font-medium text-slate-900">
                      {orderRef(row) || '--'}
                    </td>

                    {/* Client Name — the name out of the From header, not the
                        header itself: this column used to print
                        `"Ayushi Soni" <a...` and truncate mid-address. */}
                    <td className="truncate font-medium text-slate-900">
                      {clientDisplayName(row.clientName) || '--'}
                    </td>

                    {/* Vessel Name (category column goes after this one) */}
                    {/* D157.3 — the vessel's sticker colour beside it, kept visible when the name truncates. */}
                    <td title={row.vesselName || undefined}>
                      <div className="flex min-w-0 items-center gap-1.5">
                        <span className="truncate">{row.vesselName || '--'}</span>
                        <StickerColorDot color={row.vesselStickerColor} />
                      </div>
                    </td>

                    {/* Created */}
                    <td className="whitespace-nowrap text-slate-500">
                      {formatDate(row.confirmedAt)}
                    </td>

                    {/* Total Quote */}
                    <td className="text-right font-medium tabular-nums text-slate-900">
                      {formatMoney(row.totalQuote, row.currency || 'USD')}
                    </td>

                    {/* Total Items */}
                    <td className="text-right tabular-nums">
                      {row.totalItems ?? 0}
                    </td>

                    {/* Supplier POs — vendor-PO send state (D104.25), from the row's own PO facts */}
                    <td>
                      {poStatusById.has(String(row._id)) ? (
                        <StatusBadge status={ORDER_PO_STATUS_LABEL[poStatusById.get(String(row._id))!]} />
                      ) : poUnread}
                    </td>

                    {/* Quotation link */}
                    <td onClick={(e) => e.stopPropagation()}>
                      {row.quotationId ? (
                        <button
                          onClick={() => navigate(`/quotations/${row.quotationId}`)}
                          className="inline-flex items-center gap-1 whitespace-nowrap text-xs font-semibold text-primary-500 hover:underline"
                        >
                          View
                          <ExternalLink aria-hidden="true" className="h-3.5 w-3.5" />
                        </button>
                      ) : (
                        <span className="text-slate-400">—</span>
                      )}
                    </td>

                    {/* Create Vendor PO */}
                    <td onClick={(e) => e.stopPropagation()}>
                      {!row.poSummary ? (
                        poUnread
                      ) : row.poSummary.pos.length > 0 ? (
                        // A PO already exists (any status — creating POs for such an order returns
                        // the ones it has, so a button here would do nothing): a static badge
                        <span className="inline-flex items-center gap-1.5 whitespace-nowrap rounded-full border border-emerald-200 bg-emerald-50 px-2 py-1 text-xs font-medium text-emerald-800">
                          <Check aria-hidden="true" className="h-3.5 w-3.5" />
                          PO created
                        </span>
                      ) : (
                        <Button
                          variant="primary"
                          size="sm"
                          onClick={() => handleCreatePo(row._id)}
                          disabled={!!creatingId}
                          loading={creatingId === row._id}
                          icon={<ClipboardList aria-hidden="true" className="h-3.5 w-3.5" />}
                        >
                          {creatingId === row._id ? 'Creating…' : 'Create PO'}
                        </Button>
                      )}
                    </td>

                    {/* Row arrow — always visible, darkens on hover */}
                    <td className="text-slate-400 transition-colors group-hover:text-primary-500">
                      <ChevronRight aria-hidden="true" className="h-4 w-4" />
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {/* ── Pagination footer ────────────────────────────────────── */}
      {!loading && !error && (
        <div className="shrink-0 bg-white">
          <Pagination page={page} pageSize={limit} total={total} onPageChange={setPage} noun="orders" />
        </div>
      )}

      {/* ── Success modal ─────────────────────────────────────────── */}
      {successModal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-4">
          <div className="bg-white w-full max-w-sm rounded-2xl shadow-2xl overflow-hidden relative">
            {/* Close ✕ */}
            <button
              onClick={() => setSuccessModal(null)}
              className="absolute top-3 right-3 h-7 w-7 inline-flex items-center justify-center rounded-full text-slate-400 hover:text-slate-600 hover:bg-slate-100 transition-colors"
            >
              <svg xmlns="http://www.w3.org/2000/svg" className="h-4 w-4" viewBox="0 0 20 20" fill="currentColor">
                <path fillRule="evenodd" d="M4.293 4.293a1 1 0 011.414 0L10 8.586l4.293-4.293a1 1 0 111.414 1.414L11.414 10l4.293 4.293a1 1 0 01-1.414 1.414L10 11.414l-4.293 4.293a1 1 0 01-1.414-1.414L8.586 10 4.293 5.707a1 1 0 010-1.414z" clipRule="evenodd" />
              </svg>
            </button>

            <div className="px-8 pt-10 pb-8 flex flex-col items-center text-center">
              {/* Success checkmark circle */}
              <div className="h-16 w-16 rounded-full bg-cyan-50 border-4 border-cyan-100 flex items-center justify-center mb-5">
                <svg xmlns="http://www.w3.org/2000/svg" className="h-8 w-8 text-primary-500" viewBox="0 0 20 20" fill="currentColor">
                  <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
                </svg>
              </div>

              {/* Title */}
              <h2 className="text-base font-semibold leading-6 text-slate-900 mb-2">
                Vendor Purchase Order Created!
              </h2>

              {/* Description */}
              <p className="text-sm text-slate-500 leading-relaxed">
                Vendor purchase order
                {successModal.rfqRef ? (
                  <> for <span className="font-semibold text-slate-700">{successModal.rfqRef}</span></>
                ) : (
                  ` (${successModal.count} order${successModal.count !== 1 ? 's' : ''})`
                )}{' '}
                has been successfully created.
              </p>

              {/* Single centered VIEW button */}
              <button
                onClick={() => { setSuccessModal(null); navigate('/vendor-purchase-orders'); }}
                className="mt-7 w-32 py-2.5 rounded-lg bg-primary-500 text-white text-sm font-bold hover:bg-primary-600 transition-colors"
              >
                View
              </button>
            </div>
          </div>
        </div>
      )}

      {/* ── Upload modal ──────────────────────────────────────────── */}
      {/* The shared Modal: capped to the screen's height, so on a small screen the body scrolls and the
          footer (Cancel / Upload) stays on screen. The list of quotations is drawn inside the body, in flow,
          never as a popup that floats over the buttons. Only quotations that can still be confirmed are
          offered (the server filters: `unconfirmed`). */}
      <Modal
        isOpen={uploadOpen}
        title="Upload New Confirmed Order"
        onClose={closeUpload}
        maxWidthClass="max-w-md"
        bodyClass="flex min-h-0 flex-1 flex-col"
      >
        <div className="min-h-0 flex-1 space-y-2 overflow-y-auto px-4 py-4 sm:px-6">
          <label className="block text-xs font-semibold text-slate-700">Select Quotation</label>
          <QuotationSearchSelect value={pickedQuotation} onChange={setPickedQuotation} disabled={uploading} autoFocus />
          <p className="text-xs text-slate-500">Only quotations that are not confirmed yet are listed.</p>
        </div>
        <div className="flex shrink-0 flex-wrap items-center justify-end gap-2 border-t border-slate-200 px-4 py-3 sm:px-6">
          <button
            onClick={closeUpload}
            className="px-4 py-2 rounded-md border border-slate-200 text-xs font-semibold text-slate-600 hover:bg-slate-50"
          >
            Cancel
          </button>
          <button
            onClick={handleUploadNewOrder}
            disabled={!pickedQuotation || uploading}
            className="px-4 py-2 rounded-md bg-primary-500 text-white text-xs font-semibold hover:bg-primary-600 disabled:opacity-60"
          >
            {uploading ? 'Uploading...' : 'Upload'}
          </button>
        </div>
      </Modal>

      <ErrorModal
        isOpen={actionError !== null}
        title={actionError?.title || 'Error'}
        message={actionError?.message || ''}
        onClose={() => setActionError(null)}
      />
    </div>
  );
};
